import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ociRegistryApi, OCIRegistryConfig } from '../services/oci-registry-api';

const PLATFORM_OPTIONS = [
  'linux/amd64',
  'linux/arm64',
  'linux/arm/v7',
  'linux/s390x',
  'linux/ppc64le',
];

interface OCILinkDialogProps {
  open: boolean;
  onClose: () => void;
  s3Key: string;
  onLinkCreated: () => void;
}

export const OCILinkDialog: React.FC<OCILinkDialogProps> = ({ open, onClose, s3Key, onLinkCreated }) => {
  const [registries, setRegistries] = useState<OCIRegistryConfig[]>([]);
  const [registryId, setRegistryId] = useState('');
  const [repository, setRepository] = useState('');
  const [tag, setTag] = useState('');
  const [platform, setPlatform] = useState('linux/amd64');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRegistryId('');
      setRepository('');
      setTag('');
      setPlatform('linux/amd64');
      setError(null);
      ociRegistryApi.listRegistries().then(setRegistries).catch(() => {});
    }
  }, [open]);

  const handleSave = async () => {
    if (!registryId || !repository || !tag) {
      setError('Registry, repository, and tag are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const reg = registries.find(r => r.id === registryId);
      await ociRegistryApi.createLink({
        registryId,
        registryName: reg?.name || '',
        repository,
        tag,
        platform,
        s3Key,
      });
      onLinkCreated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link to OCI Registry Image</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="S3 Key"
            value={s3Key}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Registry</InputLabel>
            <Select value={registryId} onChange={(e) => setRegistryId(e.target.value)} label="Registry">
              {registries.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Repository"
            value={repository}
            onChange={(e) => setRepository(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., library/nginx"
          />
          <TextField
            label="Tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., latest"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Platform</InputLabel>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value)} label="Platform">
              {PLATFORM_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Create Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};
